import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import {
	ChevronLeftIcon,
	EyeIcon,
	LinkIcon,
	PencilIcon,
	Settings2Icon,
	Trash2Icon
} from "lucide-react"
import { AppHeader } from "~/components/AppHeader"
import { EntityIdentity } from "~/components/EntityIdentity"
import {
	EmptyState,
	ErrorState,
	LoadingState
} from "~/components/screens/StateScreen"
import { CollaborativeEditor } from "~/components/editor/CollaborativeEditor"
import { UnsavedChanges } from "~/components/UnsavedChanges"
import { FormShareDialog } from "~/components/forms/FormShareDialog"
import { Link } from "~/components/Link"
import { Button } from "~/components/ui/button"
import { Input } from "~/components/ui/input"
import { Textarea } from "~/components/ui/textarea"
import { useTeam } from "~/hooks/useTeam"
import { usePermission } from "~/hooks/usePermission"
import { TeamPermissions } from "~/lib/permissions"
import { api } from "~/trpc/react"
import { confirmAction } from "~/lib/native-dialog"

export default function FormPage() {
	const { team, isLoading, orgSlug } = useTeam()
	const { hasTeamPermission } = usePermission()
	const { formId = "" } = useParams<{ formId: string }>()
	const navigate = useNavigate()
	const utils = api.useUtils()

	const formQuery = api.forms.get.useQuery(
		{ teamId: team?.id ?? "", formId },
		{ enabled: Boolean(team?.id && formId) }
	)
	const location = useLocation()
	// /forms/:formId/edit opens straight into edit mode; the bare URL stays in View.
	const [editingFormId, setEditingFormId] = useState<string | null>(
		formId && location.pathname.endsWith("/edit") ? formId : null
	)
	const [formState, setFormState] = useState<{
		name: string
		description: string
	}>({ name: "", description: "" })

	const previousForm = useRef<typeof formQuery.data>(undefined)
	useEffect(() => {
		const data = formQuery.data
		if (!data) return
		const previous = previousForm.current
		previousForm.current = data
		setFormState((current) =>
			!previous ||
			previous.id !== data.id ||
			(current.name === previous.name &&
				current.description === previous.description)
				? { name: data.name, description: data.description }
				: current
		)
	}, [formQuery.data])

	const saveForm = api.forms.save.useMutation({
		onSuccess: async () => {
			toast("Form saved")
			await utils.forms.get.invalidate({ teamId: team?.id ?? "", formId })
			await utils.forms.list.invalidate({ teamId: team?.id ?? "" })
		},
		onError: (error) => toast.error(error.message || "Could not save the form")
	})
	const deleteForm = api.forms.delete.useMutation({
		onSuccess: () => navigate(`/${orgSlug}/${team?.slug}/forms`),
		onError: (error) =>
			toast.error(error.message || "Could not delete the form")
	})

	if (isLoading) return <LoadingState />
	if (!team || !team.enableForms)
		return <EmptyState title="Forms are disabled" />
	if (formQuery.isLoading) return <LoadingState />
	if (formQuery.error)
		return (
			<ErrorState
				title="Unable to open this form"
				description={formQuery.error.message}
			/>
		)
	if (!formQuery.data || !formState) return <LoadingState />

	const selectedForm = formQuery.data
	const canEdit = hasTeamPermission(TeamPermissions.EDIT_FORMS)
	const isEditing = canEdit && editingFormId === selectedForm.id
	const hasChanged =
		formState.name !== selectedForm.name ||
		formState.description !== selectedForm.description

	return (
		<main className="flex h-full min-h-0 flex-col">
			<AppHeader
				contentClassName="flex items-center gap-2"
				actions={
					<>
						{canEdit && (
							<Button
								variant="outline"
								size="sm"
								onClick={() => {
									if (isEditing && hasChanged) {
										toast.info(
											"Save or reset your changes before switching back to View."
										)
										return
									}
									setEditingFormId(isEditing ? null : selectedForm.id)
								}}
							>
								{isEditing ? (
									<EyeIcon className="size-4" />
								) : (
									<PencilIcon className="size-4" />
								)}
								{isEditing ? "View" : "Edit form"}
							</Button>
						)}
						{hasTeamPermission(TeamPermissions.VIEW_FORM_SUBMISSIONS) && (
							<Button variant="ghost" size="sm" asChild>
								<Link
									to={`/${orgSlug}/${team.slug}/forms/${formId}/submissions`}
								>
									Submissions
								</Link>
							</Button>
						)}
						{canEdit && (
							<FormShareDialog teamId={team.id} formId={formId}>
								<Button variant="ghost" size="sm">
									<LinkIcon className="size-4" />
									Share
								</Button>
							</FormShareDialog>
						)}
						{hasTeamPermission(TeamPermissions.ADMINISTRATOR) && (
							<Button variant="ghost" size="icon-md" asChild>
								<Link
									to={`/${orgSlug}/${team.slug}/forms/${formId}/settings`}
									aria-label="Form settings"
								>
									<Settings2Icon />
								</Link>
							</Button>
						)}
						{canEdit && (
							<Button
								variant="destructive-ghost"
								size="icon-md"
								aria-label="Delete form"
								disabled={deleteForm.isPending}
								onClick={async () => {
									if (
										await confirmAction({
											title: "Delete form",
											message: "Delete this form and all of its submissions?"
										})
									)
										deleteForm.mutate({ teamId: team.id, formId })
								}}
							>
								<Trash2Icon className="size-4" />
							</Button>
						)}
					</>
				}
			>
				<Button
					variant="ghost"
					size="icon"
					aria-label="Back to forms"
					onClick={() => navigate(`/${orgSlug}/${team.slug}/forms`)}
				>
					<ChevronLeftIcon className="size-4" />
				</Button>
				<EntityIdentity kind="team" name={team.name} image={team.logo} />
				<span className="text-text-sub">/</span>
				<span className="font-medium">Forms</span>
			</AppHeader>
			<div className="min-h-0 flex-1 overflow-y-auto px-7 py-5">
				<div className="mx-auto flex max-w-4xl flex-col gap-3">
					{isEditing ? (
						<>
							<Input
								value={formState.name}
								onChange={(event) =>
									setFormState({ ...formState, name: event.target.value })
								}
								className="text-3xl"
								placeholder="Form title"
								variant="title"
							/>
							<Textarea
								value={formState.description}
								onChange={(event) =>
									setFormState({
										...formState,
										description: event.target.value
									})
								}
								placeholder="Short form description"
								variant="summary"
							/>
						</>
					) : (
						<article className="flex flex-col gap-4">
							<h2 className="text-3xl font-semibold break-words">
								{selectedForm.name}
							</h2>
							{selectedForm.description && (
								<p className="text-text-sub whitespace-pre-wrap">
									{selectedForm.description}
								</p>
							)}
						</article>
					)}
					<div className="mt-2">
						<CollaborativeEditor
							key={selectedForm.id}
							kind="form"
							id={selectedForm.id}
							variant="form"
							teamId={team.id}
							disabled={!isEditing}
							placeholder="Type / to insert a question"
							initialState={selectedForm.content}
							onSaved={() => {
								void utils.forms.get.invalidate({ teamId: team.id, formId })
							}}
						/>
					</div>
				</div>
			</div>
			<UnsavedChanges
				isVisible={canEdit && hasChanged}
				onSave={async () => {
					if (!team) return
					await saveForm.mutateAsync({
						id: selectedForm.id,
						teamId: team.id,
						name: formState.name,
						description: formState.description,
						updatedAt: formQuery.data.updatedAt
					})
				}}
				onReset={async () => {
					setFormState({
						name: selectedForm.name,
						description: selectedForm.description
					})
					await utils.forms.get.invalidate({ teamId: team.id, formId })
				}}
			/>
		</main>
	)
}
